import {
  createLoginFlow,
  getLoginFlow,
  updateLoginFlow,
  createRegistrationFlow,
  getRegistrationFlow,
  updateRegistrationFlow,
  createRecoveryFlow,
  getRecoveryFlow,
  createVerificationFlow,
  getVerificationFlow,
  createLogoutFlow,
  performLogout,
  toSession,
  isSessionValid,
  kratosApi,
  RecoveryFlowSchema,
  type LoginFlow,
  type RegistrationFlow,
  type RecoveryFlow,
  type VerificationFlow,
  type Session,
} from '@/lib/api/kratos';
import {
  LoginFormValuesSchema,
  LoginResponseSchema,
  RegistrationFormValuesSchema,
  RegistrationResponseSchema,
  RecoveryFormValuesSchema,
  RecoveryResponseSchema,
  VerificationFormValuesSchema,
  VerificationResponseSchema,
  LogoutResponseSchema,
  SessionValidationResponseSchema,
  type LoginFormValues,
  type LoginResponse,
  type RegistrationFormValues,
  type RegistrationResponse,
  type RecoveryFormValues,
  type RecoveryResponse,
  type VerificationFormValues,
  type VerificationResponse,
  type LogoutResponse,
  type SessionValidationResponse,
} from './auth.schemas';

// Re-export types for use in hooks and components
export type {
  LoginFormValues,
  LoginResponse,
  RegistrationFormValues,
  RegistrationResponse,
  RecoveryFormValues,
  RecoveryResponse,
  VerificationFormValues,
  VerificationResponse,
  LogoutResponse,
  SessionValidationResponse,
};

// ============================================================================
// Login API Functions
// ============================================================================

/**
 * Initialize a login flow
 * @param params - Optional parameters for the login flow
 * @returns Promise<LoginFlow> - The initialized login flow
 */
export async function initializeLogin(params?: {
  refresh?: boolean;
  aal?: 'aal1' | 'aal2';
  returnTo?: string;
}): Promise<LoginFlow> {
  const flow = await createLoginFlow(params);
  return flow;
}

/**
 * Get an existing login flow by ID
 * @param flowId - The flow ID from URL query parameter
 * @returns Promise<LoginFlow> - The login flow
 */
export async function fetchLoginFlow(flowId: string): Promise<LoginFlow> {
  const flow = await getLoginFlow(flowId);
  return flow;
}

/**
 * Submit login credentials - matches original Nuxt2 implementation
 * @param flow - The login flow object
 * @param values - The login form values
 * @returns Promise<LoginResponse> - The login response with session or updated flow
 */
export async function submitLogin(
  flow: LoginFlow,
  values: LoginFormValues
): Promise<LoginResponse> {
  // Validate input with Zod - but allow partial data for identifier_first step
  const validatedValues = LoginFormValuesSchema.parse(values);

  // Build the request body exactly like the original Nuxt2 implementation
  const requestBody: Record<string, unknown> = {
    method: validatedValues.method,
  };

  // Add fields only if they have values (important for identifier_first step)
  if (validatedValues.identifier) {
    requestBody.identifier = validatedValues.identifier;
  }
  
  if (validatedValues.password) {
    requestBody.password = validatedValues.password;
  }
  
  if (validatedValues.csrf_token) {
    requestBody.csrf_token = validatedValues.csrf_token;
  }

  console.log('Submitting login with payload:', requestBody);

  // Submit to Kratos using @ory/client like the original implementation
  // Note: For identifier_first, this may throw a 400 error which is NORMAL
  console.log('Calling updateLoginFlow...');
  const response = await updateLoginFlow(flow, requestBody as any);
  console.log('updateLoginFlow returned:', response);

  // Validate response with Zod
  const validatedResponse = LoginResponseSchema.parse(response);
  console.log('Validated response:', validatedResponse);
  return validatedResponse;
}

// ============================================================================
// Registration API Functions
// ============================================================================

/**
 * Initialize a registration flow
 * @param params - Optional parameters for the registration flow
 * @returns Promise<RegistrationFlow> - The initialized registration flow
 */
export async function initializeRegistration(params?: {
  returnTo?: string;
}): Promise<RegistrationFlow> {
  const flow = await createRegistrationFlow(params);
  return flow;
}

/**
 * Get an existing registration flow by ID
 * @param flowId - The flow ID from URL query parameter
 * @returns Promise<RegistrationFlow> - The registration flow
 */
export async function fetchRegistrationFlow(flowId: string): Promise<RegistrationFlow> {
  const flow = await getRegistrationFlow(flowId);
  return flow;
}

/**
 * Submit registration data
 * @param flowId - The flow ID
 * @param values - The registration form values
 * @returns Promise<RegistrationResponse> - The registration response with session or updated flow
 */
export async function submitRegistration(
  flowId: string,
  values: RegistrationFormValues
): Promise<RegistrationResponse> {
  // Validate input with Zod
  const validatedValues = RegistrationFormValuesSchema.parse(values);

  // Submit to Kratos
  const response = await updateRegistrationFlow(flowId, {
    method: validatedValues.method,
    traits: validatedValues.traits,
    password: validatedValues.password,
    csrf_token: validatedValues.csrf_token,
  });

  // Validate response with Zod
  return RegistrationResponseSchema.parse(response);
}

// ============================================================================
// Recovery API Functions
// ============================================================================

/**
 * Initialize a recovery flow (password reset)
 * @param params - Optional parameters for the recovery flow
 * @returns Promise<RecoveryFlow> - The initialized recovery flow
 */
export async function initializeRecovery(params?: {
  returnTo?: string;
}): Promise<RecoveryFlow> {
  const flow = await createRecoveryFlow(params);
  return flow;
}

/**
 * Get an existing recovery flow by ID
 * @param flowId - The flow ID from URL query parameter
 * @returns Promise<RecoveryFlow> - The recovery flow
 */
export async function fetchRecoveryFlow(flowId: string): Promise<RecoveryFlow> {
  const flow = await getRecoveryFlow(flowId);
  return flow;
}

/**
 * Submit recovery request (send password reset email)
 * @param flowId - The flow ID
 * @param values - The recovery form values
 * @returns Promise<RecoveryResponse> - The recovery response
 */
export async function submitRecovery(
  flowId: string,
  values: RecoveryFormValues
): Promise<RecoveryResponse> {
  // Validate input with Zod
  RecoveryFormValuesSchema.parse(values);

  console.log('Submitting recovery request:', { flowId, values });

  try {
    // Use @ory/client FrontendApi to submit recovery flow
    const response = await kratosApi.updateRecoveryFlow({
      flow: flowId,
      updateRecoveryFlowBody: values as any,
    });

    console.log('Recovery submission response:', response.data);

    // Check if this is a successful completion (no more flow needed)
    if (response.data.return_to) {
      // Recovery completed successfully
      return RecoveryResponseSchema.parse({
        flow: null,
        state: 'passed_challenge',
      });
    }

    // Parse the response - it should be a flow response (updated flow)
    if (response.data.id) {
      const flow = RecoveryFlowSchema.parse(response.data);
      
      // Determine state based on flow content
      let state: 'choose_method' | 'sent_email' | 'passed_challenge' | 'error' = 'choose_method';
      
      if (flow.state === 'sent_email' || flow.ui.messages?.some(m => m.text.includes('sent'))) {
        state = 'sent_email';
      } else if (flow.ui.nodes.some(n => n.attributes.name === 'password')) {
        state = 'passed_challenge'; // Ready to set new password
      } else if (flow.ui.nodes.some(n => n.attributes.name === 'code')) {
        state = 'sent_email'; // Ready to enter code
      }
      
      return RecoveryResponseSchema.parse({
        flow,
        state,
      });
    }

    // Fallback - assume success if no flow returned
    return RecoveryResponseSchema.parse({
      flow: null,
      state: 'passed_challenge',
    });
  } catch (error: any) {
    console.error('Recovery submission error:', error);
    console.error('Error response:', error.response?.data);

    // Handle special case: browser_location_change_required (422)
    // This happens when recovery code is valid and user needs to be redirected to settings
    if (error.response?.status === 422 && 
        error.response?.data?.error?.id === 'browser_location_change_required' &&
        error.response?.data?.redirect_browser_to) {
      console.log('Recovery code validated, redirecting to settings');
      return RecoveryResponseSchema.parse({
        flow: null,
        state: 'passed_challenge',
        redirect_url: error.response.data.redirect_browser_to,
      });
    }

    // Handle Kratos error responses with flow data
    if (error.response?.data?.id) {
      // Error response contains updated flow
      const flow = RecoveryFlowSchema.parse(error.response.data);
      return RecoveryResponseSchema.parse({
        flow,
        state: 'error',
      });
    }

    throw error;
  }
}

// ============================================================================
// Verification API Functions
// ============================================================================

/**
 * Initialize a verification flow (email verification)
 * @param params - Optional parameters for the verification flow
 * @returns Promise<VerificationFlow> - The initialized verification flow
 */
export async function initializeVerification(params?: {
  returnTo?: string;
}): Promise<VerificationFlow> {
  const flow = await createVerificationFlow(params);
  return flow;
}

/**
 * Get an existing verification flow by ID
 * @param flowId - The flow ID from URL query parameter
 * @returns Promise<VerificationFlow> - The verification flow
 */
export async function fetchVerificationFlow(flowId: string): Promise<VerificationFlow> {
  const flow = await getVerificationFlow(flowId);
  return flow;
}

/**
 * Submit verification request
 * @param flowId - The flow ID
 * @param values - The verification form values
 * @returns Promise<VerificationResponse> - The verification response
 */
export async function submitVerification(
  flowId: string,
  values: VerificationFormValues
): Promise<VerificationResponse> {
  // Validate input with Zod
  VerificationFormValuesSchema.parse(values);

  // Note: Kratos verification flow submission is handled differently
  // This is a simplified version - actual implementation may vary
  const flow = await getVerificationFlow(flowId);

  // Validate response with Zod
  return VerificationResponseSchema.parse({
    flow,
    state: 'sent_email',
  });
}

// ============================================================================
// Logout API Functions
// ============================================================================

/**
 * Initialize and perform logout
 * @returns Promise<LogoutResponse> - The logout response
 */
export async function logout(): Promise<LogoutResponse> {
  // Create logout flow
  const logoutFlow = await createLogoutFlow();

  // Validate response with Zod
  const validatedFlow = LogoutResponseSchema.parse(logoutFlow);

  // Perform logout
  await performLogout(validatedFlow.logout_token);

  return validatedFlow;
}

// ============================================================================
// Session API Functions
// ============================================================================

/**
 * Get the current session
 * @returns Promise<Session> - The current session
 */
export async function getCurrentSession(): Promise<Session> {
  const session = await toSession();
  return session;
}

/**
 * Validate the current session
 * @returns Promise<SessionValidationResponse> - Session validation result
 */
export async function validateSession(): Promise<SessionValidationResponse> {
  try {
    const session = await toSession();
    const isValid = isSessionValid(session);

    // Validate response with Zod
    return SessionValidationResponseSchema.parse({
      session,
      isValid,
    });
  } catch (error) {
    throw error;
  }
}

/**
 * Check if user is authenticated
 * @returns Promise<boolean> - True if authenticated
 */
export async function checkAuthentication(): Promise<boolean> {
  try {
    const session = await toSession();
    return isSessionValid(session);
  } catch (error) {
    return false;
  }
}
